import React from 'react';
import { Check, Clock, CreditCard, Package } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TimelineStep {
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  completed: boolean;
  active: boolean;
  detail?: string;
}

interface RegistrationTimelineProps {
  registrationStatus: string;
  paymentStatus: string;
  bibPickedUp: boolean;
  createdAt: string;
}

const RegistrationTimeline: React.FC<RegistrationTimelineProps> = ({
  registrationStatus,
  paymentStatus,
  bibPickedUp,
  createdAt,
}) => {
  const isConfirmed = registrationStatus === 'Confirmed';
  const isCancelled = registrationStatus === 'Cancelled';
  const isPaid = paymentStatus === 'Paid';

  const steps: TimelineStep[] = [
    {
      label: 'Регистриран',
      icon: Check,
      completed: true,
      active: !isConfirmed && !isCancelled,
      detail: new Date(createdAt).toLocaleDateString('mk-MK'),
    },
    {
      label: 'Потврден',
      icon: Check,
      completed: isConfirmed,
      active: isConfirmed && !isPaid,
    },
    {
      label: 'Платено',
      icon: CreditCard,
      completed: isPaid,
      active: isPaid && !bibPickedUp,
    },
    {
      label: 'Број преземен',
      icon: Package,
      completed: bibPickedUp,
      active: bibPickedUp,
    },
  ];

  if (isCancelled) {
    return (
      <div className="flex items-center gap-2 py-3 px-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="w-6 h-6 rounded-full bg-red-500 flex items-center justify-center">
          <span className="text-white text-xs font-bold">✕</span>
        </div>
        <span className="text-sm font-medium text-red-700">Регистрацијата е откажана</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-0 py-3">
      {steps.map((step, idx) => (
        <React.Fragment key={idx}>
          <div className="flex flex-col items-center min-w-0">
            <div
              className={cn(
                'w-8 h-8 rounded-full flex items-center justify-center shrink-0 transition-colors',
                step.completed
                  ? 'bg-green-500 text-white'
                  : 'bg-gray-200 text-gray-400'
              )}
            >
              <step.icon className="w-4 h-4" />
            </div>
            <span
              className={cn(
                'text-[11px] mt-1.5 text-center leading-tight whitespace-nowrap',
                step.completed ? 'text-green-700 font-medium' : 'text-gray-400'
              )}
            >
              {step.label}
            </span>
            {step.detail && (
              <span className="text-[10px] text-gray-400">{step.detail}</span>
            )}
          </div>
          {idx < steps.length - 1 && (
            <div
              className={cn(
                'h-0.5 flex-1 min-w-4 mt-[-16px]',
                steps[idx + 1].completed ? 'bg-green-500' : 'bg-gray-200'
              )}
            />
          )}
        </React.Fragment>
      ))}
    </div>
  );
};

export default RegistrationTimeline;
